<template>
  <section class="container agent-commission-panel" aria-labelledby="agent-commission-title">
    <header class="agent-commission-header">
      <div><h1 id="agent-commission-title">代理中心</h1><p>查看佣金钱包及每月发放、调整记录</p></div>
      <button type="button" class="agent-record-button" :disabled="summaryLoading || recordsLoading" @click="refresh">刷新数据</button>
    </header>
    <div v-if="summaryLoading && !summary" class="agent-record-state" role="status">正在加载代理概览…</div>
    <div v-else-if="summaryError" class="agent-record-state error" role="alert"><p>{{ summaryError }}</p><button type="button" class="agent-record-button" @click="refresh">重新获取</button></div>
    <template v-else-if="summary">
      <div class="agent-commission-overview">
        <div class="agent-commission-identity"><b>{{ summary.username }}</b><span>{{ summary.agentNo }}</span></div>
        <dl><div><dt>佣金钱包余额（元）</dt><dd>{{ formatDecimal(summary.availableBalance) }}</dd></div><div><dt>待结算佣金（元）</dt><dd>{{ formatDecimal(summary.pendingCommission) }}</dd></div></dl>
      </div>
      <div class="agent-record-toolbar">
        <nav class="agent-record-tabs" aria-label="代理记录类型">
          <button type="button" :class="{ active: activeTab === 'commission' }" :aria-pressed="activeTab === 'commission'" @click="selectTab('commission')">佣金记录</button>
          <button type="button" :class="{ active: activeTab === 'settlement' }" :aria-pressed="activeTab === 'settlement'" @click="selectTab('settlement')">结算记录</button>
        </nav>
        <div class="agent-record-filter"><label for="agent-record-month">结算月份</label><input id="agent-record-month" type="month" :value="month" @change="selectMonth($event.target.value)" /><button type="button" class="agent-record-button" :disabled="!month" @click="selectMonth('')">全部月份</button></div>
      </div>
      <p class="agent-record-description">{{ activeTab === 'commission' ? '金额保留调整方向，负数表示扣减。' : '结算金额为实际到账净额，包含已完成的佣金调整。' }}</p>
      <div v-if="recordsLoading" class="agent-record-state" role="status">正在加载{{ activeTab === 'commission' ? '佣金' : '结算' }}记录…</div>
      <div v-else-if="recordsError" class="agent-record-state error" role="alert"><p>{{ recordsError }}</p><button type="button" class="agent-record-button" @click="loadRecords(page)">重新获取</button></div>
      <div v-else-if="!records.length" class="agent-record-state">{{ month ? `${month} 暂无${activeTab === 'commission' ? '佣金' : '结算'}记录` : `暂无${activeTab === 'commission' ? '佣金' : '结算'}记录` }}</div>
      <div v-else class="agent-record-table-scroll">
        <table class="agent-record-table">
          <thead><tr><th scope="col">结算月份</th><th scope="col">{{ activeTab === 'commission' ? '佣金类型' : '结算单号' }}</th><th scope="col" class="amount">{{ activeTab === 'commission' ? '佣金金额（元）' : '结算金额（元）' }}</th><th scope="col">状态</th><th scope="col">{{ activeTab === 'commission' ? '发放时间' : '完成时间' }}</th></tr></thead>
          <tbody><tr v-for="row in records" :key="row.id"><td>{{ row.settlementPeriod }}</td><td>{{ activeTab === 'commission' ? agentCommissionTypeLabel(row.commissionType) : row.settlementNo }}</td><td class="amount" :class="{ negative: String(recordAmount(row)).startsWith('-') && formatDecimal(recordAmount(row)) !== '0.00' }">{{ formatDecimal(recordAmount(row)) }}</td><td><span :class="['agent-record-status', { complete: row.status === 3 }]">{{ agentRecordStatusLabel(row.status, activeTab) }}</span></td><td>{{ agentRecordTimeLabel(activeTab === 'commission' ? row.releasedAt : row.completedAt) }}</td></tr></tbody>
        </table>
      </div>
      <nav v-if="!recordsLoading && !recordsError && total > 0" class="agent-record-pagination" aria-label="代理记录分页"><span>共 {{ total }} 条，第 {{ page }} / {{ pageCount }} 页</span><button type="button" class="agent-record-button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button><button type="button" class="agent-record-button" :disabled="page >= pageCount" @click="changePage(page + 1)">下一页</button></nav>
    </template>
  </section>
</template>

<script setup>
import { onBeforeUnmount, watch } from 'vue'
import { webApi } from '../services/webApi'
import { authStorage } from '../services/http'
import { useAgentCommissionRecords } from '../composables/useAgentCommissionRecords.js'
import { formatDecimal } from '../utils/formatDecimal.js'
import { agentCommissionTypeLabel, agentRecordStatusLabel, agentRecordTimeLabel } from '../utils/agentCommission.js'

const props = defineProps({ account: { type: String, default: '' } })
const flow = useAgentCommissionRecords({
  getToken: authStorage.getToken,
  fetchSummary: webApi.agentSummary,
  fetchCommissions: webApi.agentCommissions,
  fetchSettlements: webApi.agentSettlements,
})
const {
  summary, summaryLoading, summaryError, activeTab, month,
  records, recordsLoading, recordsError, total, page, pageCount,
  refresh, selectTab, selectMonth, changePage, loadRecords,
} = flow
function recordAmount(row) {
  return activeTab.value === 'commission' ? row.commissionAmount : row.settlementAmount
}
watch(() => props.account, refresh, { immediate: true })
onBeforeUnmount(flow.clear)
</script>

<style scoped>
.agent-commission-panel { padding: 46px 0 80px; }
.agent-commission-header { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 28px; }
.agent-commission-header h1 { margin: 0 0 8px; font-size: 30px; font-weight: 600; letter-spacing: -.5px; }
.agent-commission-header p, .agent-record-description { margin: 0; color: #6e7f98; font-size: 14px; line-height: 1.6; }
.agent-commission-overview { display: flex; align-items: center; justify-content: space-between; gap: 28px; padding: 28px 32px; border-radius: 16px; background: #fff; }
.agent-commission-identity { display: grid; gap: 8px; overflow-wrap: anywhere; }
.agent-commission-identity b { font-size: 20px; font-weight: 600; }
.agent-commission-identity span { color: #6e7f98; font-size: 13px; }
.agent-commission-overview dl { display: flex; gap: 64px; margin: 0; }
.agent-commission-overview dt { color: #6e7f98; font-size: 13px; }
.agent-commission-overview dd { margin: 9px 0 0; color: #253f60; font-size: 28px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.agent-record-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin: 32px 0 16px; border-bottom: 1px solid #dce8f5; }
.agent-record-tabs { display: flex; gap: 24px; }
.agent-record-tabs button { padding: 16px 0; border-bottom: 3px solid transparent; color: #6e7f98; background: transparent; font-size: 17px; }
.agent-record-tabs button.active { border-color: #179af8; color: #177fc5; font-weight: 600; }
.agent-record-filter { display: flex; align-items: center; gap: 12px; color: #6e7f98; font-size: 13px; }
.agent-record-filter input { max-width: 180px; padding: 9px 12px; border: 1px solid #dce8f5; border-radius: 8px; color: #253f60; background: #fff; }
.agent-record-button { flex-shrink: 0; padding: 9px 14px; border: 1px solid #cfdfef; border-radius: 8px; color: #315775; background: #fff; transition: background .15s; }
.agent-record-button:hover:enabled { background: #eef6fc; }
.agent-record-button:disabled { opacity: .45; cursor: default; }
button:focus-visible, input:focus-visible { outline: 2px solid #179af8; outline-offset: 3px; }
.agent-record-description { margin-bottom: 16px; font-size: 13px; }
.agent-record-table-scroll { overflow-x: auto; border: 1px solid #dce8f5; border-radius: 12px; background: #fff; }
.agent-record-table { width: 100%; min-width: 720px; border-collapse: collapse; color: #31506e; font-size: 14px; }
.agent-record-table th, .agent-record-table td { padding: 18px 22px; text-align: left; white-space: nowrap; }
.agent-record-table th { color: #6e7f98; background: #f1f6fc; font-size: 13px; font-weight: 500; }
.agent-record-table td { border-top: 1px solid #e8eff7; }
.agent-record-table .amount { text-align: right; font-variant-numeric: tabular-nums; }
.agent-record-table .negative { color: #b84c4c; }
.agent-record-status { color: #6e7f98; }
.agent-record-status.complete { color: #247253; }
.agent-record-state { display: grid; justify-items: center; gap: 14px; padding: 48px 20px; border-radius: 12px; color: #6e7f98; background: #fff; text-align: center; line-height: 1.7; }
.agent-record-state p { margin: 0; }
.agent-record-state.error { color: #a64b48; }
.agent-record-pagination { display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-top: 22px; }
.agent-record-pagination span { margin-right: auto; color: #6e7f98; font-size: 13px; }
@media (max-width: 800px) {
  .agent-commission-overview { display: block; padding: 24px; }
  .agent-commission-overview dl { margin-top: 24px; gap: 28px; flex-wrap: wrap; }
  .agent-record-toolbar { flex-wrap: wrap; padding-bottom: 12px; gap: 8px; }
  .agent-record-filter { flex-wrap: wrap; }
}
@media (max-width: 480px) {
  .agent-commission-panel { padding-top: 30px; }
  .agent-commission-header { align-items: flex-start; gap: 12px; }
  .agent-commission-header h1 { font-size: 26px; }
  .agent-commission-overview dd { font-size: 24px; }
  .agent-record-pagination { flex-wrap: wrap; }
  .agent-record-pagination span { width: 100%; }
}
</style>
